import React, { useMemo, useState } from 'react';

import { useCollection } from '@amzn/awsui-collection-hooks';
import Table from '@amzn/awsui-components-console/table';
import Header from '@amzn/awsui-components-console/header';
import Box from '@amzn/awsui-components-console/box';
import Alert from '@amzn/awsui-components-console/alert';
import Button from '@amzn/awsui-components-console/button';
import Pagination from '@amzn/awsui-components-console/pagination';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { useNavigate } from 'react-router';

import InfoTooltip from 'src/common/ui/InfoTooltip';
import {
  TablePreferences,
  useTablePreferences,
} from 'src/common/ui/TablePreferences';

import { HistoricalTranslationJob } from '../types';
import { trackPaginationAriaLabels, TRACK_STRING_IDS } from '../constants';
import {
  createHistoricalColumnDefinitions,
  OLDEST_FIRST_SORTING_STATE,
} from './columns';
import { historicalJobsPreferences } from './trackTablePreferences';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface HistoricalJobsTableProps {
  /** Every job loaded so far — sorting and paging are client-side over these. */
  items: HistoricalTranslationJob[];
  loading: boolean;
  /** True while a `Load more` fetch is in flight. */
  isLoadingMore: boolean;
  /** True when older jobs remain unloaded on the server. */
  hasMore: boolean;
  onLoadMore: () => void;
}

const HistoricalJobsTable: React.FC<HistoricalJobsTableProps> = ({
  items,
  loading,
  isLoadingMore,
  hasMore,
  onLoadMore,
}) => {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const columnDefinitions = useMemo(
    // `t` is a dependency: without it the headers would keep their old labels
    // after a locale change, since the memo would never recompute. `locale` is
    // one for the same reason — the column cells format dates through it.
    () => createHistoricalColumnDefinitions(t, locale, navigate),
    [t, locale, navigate],
  );

  const [userSorted, setUserSorted] = useState(false);

  // `useCollection` only CLAMPS the page index on a size change, so reset it here.
  // `actions` is read lazily: it comes from the call below, which needs this size.
  const { preferences, onConfirm, columnDisplay } = useTablePreferences(
    historicalJobsPreferences(t),
    { onPageSizeChange: () => actions.setCurrentPage(1) },
  );

  const {
    items: pageItems,
    paginationProps,
    collectionProps,
    actions,
  } = useCollection(items, {
    // Always a number: the config declares page sizes.
    pagination: { pageSize: preferences.pageSize },
    sorting: { defaultState: OLDEST_FIRST_SORTING_STATE },
  });

  const showSortNotice = hasMore && userSorted;

  /*
   * Count in the heading TEXT, as the namespace page's "String groups (4)" does,
   * so it inherits the heading's weight where Cloudscape's `counter` prop renders a
   * lighter metadata span. Counts every row LOADED, not the visible page, so paging
   * does not change it; the query is `nextToken`-paginated with no total, so '+'
   * marks a lower bound.
   */
  const headerCount =
    loading || items.length === 0
      ? ''
      : ` (${items.length}${hasMore ? '+' : ''})`;

  return (
    <Table<HistoricalTranslationJob>
      {...collectionProps}
      onSortingChange={(event) => {
        setUserSorted(true);
        collectionProps.onSortingChange?.(event);
      }}
      variant="container"
      trackBy="trackingId"
      ariaLabels={{
        tableLabel: t('track-historical-table-aria'),
      }}
      stickyHeader
      stickyColumns={{ first: 1 }}
      wrapLines={preferences.wrapLines}
      stripedRows={preferences.stripedRows}
      contentDensity={preferences.contentDensity}
      resizableColumns
      loading={loading}
      loadingText={t(TRACK_STRING_IDS.HISTORICAL_JOBS_LOADING)}
      skeleton={loading ? { totalRows: 5 } : undefined}
      header={
        <SpaceBetween size="xs">
          <Header
            variant="h2"
            // Cloudscape's own slot for explanatory text: it renders the trigger as
            // a sibling of the heading tag, so the help text stays out of the
            // heading's accessible name, and Cloudscape owns the gap.
            info={
              <InfoTooltip content={t(TRACK_STRING_IDS.HISTORICAL_JOBS_INFO)} />
            }
          >
            {t(TRACK_STRING_IDS.HISTORICAL_JOBS_HEADER)}
            {headerCount}
          </Header>
          {showSortNotice && (
            <Alert
              type="info"
              dismissible
              dismissAriaLabel={t(TRACK_STRING_IDS.SORT_WINDOW_NOTICE_DISMISS)}
              onDismiss={() => setUserSorted(false)}
            >
              {t(TRACK_STRING_IDS.SORT_WINDOW_NOTICE, {
                loadedCount: items.length,
              })}
            </Alert>
          )}
        </SpaceBetween>
      }
      pagination={
        items.length > 0 ? (
          <SpaceBetween direction="horizontal" size="xs">
            {hasMore && (
              <Button
                loading={isLoadingMore}
                disabled={isLoadingMore}
                onClick={onLoadMore}
              >
                {t(TRACK_STRING_IDS.LOAD_MORE)}
              </Button>
            )}
            <Pagination
              {...paginationProps}
              ariaLabels={trackPaginationAriaLabels(t)}
            />
          </SpaceBetween>
        ) : undefined
      }
      columnDefinitions={columnDefinitions}
      columnDisplay={columnDisplay}
      // Not gated on `items.length`: preferences apply over an empty table too.
      preferences={
        <TablePreferences
          config={historicalJobsPreferences(t)}
          preferences={preferences}
          onConfirm={onConfirm}
        />
      }
      items={pageItems}
      empty={
        <Box textAlign="center" color="inherit">
          <SpaceBetween size="m">
            <Box variant="strong">
              {t(TRACK_STRING_IDS.HISTORICAL_JOBS_EMPTY_TITLE)}
            </Box>
            <Box variant="p" color="inherit">
              {t(TRACK_STRING_IDS.HISTORICAL_JOBS_EMPTY_BODY)}
            </Box>
          </SpaceBetween>
        </Box>
      }
    />
  );
};

export default HistoricalJobsTable;
